/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getPercepcionesClasesDeContrataciones,
  postPercepcionClaseDeContratacion,
  deletePercepcionClaseDeContratacion,
  patchPercepcionClaseDeContratacion,
  getPercepcionClaseDeContratacion,
} from 'api/nominas/percepciones/percepcionesClasesDeContrataciones';
import { checkToken } from 'api/utils';
import {
  getClasesDeContrataciones,
} from 'api/nominas/catalogos/clasesDeContrataciones';

export const percepcionClaseDeContratacionPermission = {
  permissionModel: 'percepcionclasedecontratacion',
  catalogsModels: [
    'percepcion',
  ],
  tabsModels: [],
};

const PercepcionClaseDeContratacion = ({
  permission,
  selected,
  estatusAutorizadoInactivo,
}) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [clases, setClases] = useState([]);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPercepcionesClasesDeContrataciones({ percepciones: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _clases,
        _data,
      ] = await Promise.all([
        getClasesDeContrataciones(),
        getPercepcionesClasesDeContrataciones({ percepciones: selected }),
      ]);
      if (mounted) {
        setClases(_clases);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _claseContratacion = await getPercepcionClaseDeContratacion(match.id);
    setTimeout(() => {
      if (_claseContratacion) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_claseContratacion));
          formDatos.setFieldsValue(formatReceived(_claseContratacion));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchPercepcionClaseDeContratacion(key, { ...values, percepciones: selected }) : postPercepcionClaseDeContratacion({ ...values, percepciones: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionClaseDeContratacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clase de Contratación',
      dataIndex: 'clase_de_contratacion',
      key: 'clase_de_contratacion',
      width: 400,
      render: (val) => clases.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizadoInactivo}
          disableDelete={estatusAutorizadoInactivo}
          disableEdit={estatusAutorizadoInactivo}
          allowDoubleClick={estatusAutorizadoInactivo === false}
          permission={permission}
          mobileColIndex={0}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Clase de Contratación`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="50%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clase_de_contratacion"
                label="Clase de Contratación"
                rules={rules.estatus}
                hasFeedback
              >
                <Select
                  dataSource={clases}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Clase de Contratación ${clases.find((e) => e.id === form.getFieldValue('clase_de_contratacion'))?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionClaseDeContratacion;
